import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CCol, CRow, CForm, CFormInput, CFormLabel, CFormSelect, CFormTextarea, CButton, CAlert,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CSpinner,
  CCard, CCardHeader, CCardBody, CInputGroup, CInputGroupText,
  CTabs, CTabList, CTab, CTabPanel,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilUser, cilPhone, cilLocationPin, cilMap, cilAddressBook, cilUserFollow, cilPencil,
  cilArrowLeft, cilPen, cilTrash, cilWarning,
} from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { prefijoPais } from '../utils/whatsapp'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import { usePendiente } from '../components/ww/pendiente'
import FirmaCanvas from '../components/ww/FirmaCanvas'

const estados = ['Nuevo', 'Activo', 'Inactivo', 'Pausado', 'Eliminado']

// Vista de cliente — réplica de resumencliente_level_detail.cs:79 (grillas de
// préstamos y facturas del cliente) y cliente_bc.cs:191 (IsIns del BC de alta).
// La cédula es el id numérico del contrato GX, sin separadores.
const columnasPrestamos: WwColumn[] = [
  { key: 'prestamoNro', title: 'Nro', type: 'number' },
  // [RGM] 2026-10-07 (feature factura-credito-unificado T6 — pedido Rafael:
  // "en el detalle del cliente mostramos prestamos # y factura #"): el crédito
  // de factura muestra su factura ligada (PrestamoFacturaNro; vacío si no).
  {
    key: 'prestamoFacturaNro',
    title: 'Factura',
    type: 'number',
    render: (fila: any) =>
      Number(fila.prestamoFacturaNro ?? 0) > 0 ? fila.prestamoFacturaNro : '',
  },
  { key: 'prestamoFecha', title: 'Fecha', type: 'date' },
  { key: 'cobrador', title: 'Cobrador' },
  { key: 'prestamoTipo', title: 'Tipo' },
  { key: 'prestamoMPago', title: 'Modalidad', type: 'number' },
  { key: 'prestamoValor', title: 'Valor', type: 'currency' },
  { key: 'prestamoCuotas', title: 'Cuotas', type: 'number' },
  { key: 'prestamoCuota', title: 'Cuota', type: 'currency' },
  { key: 'prestamoSaldo', title: 'Saldo', type: 'currency' },
  { key: 'prestamoProximoCobro', title: 'Proximo cobro', type: 'date' },
]

const columnasFacturas: WwColumn[] = [
  { key: 'facturaNro', title: 'Nro', type: 'number' },
  { key: 'facturaFecha', title: 'Fecha', type: 'date' },
  { key: 'facturaEstado', title: 'Estado', type: 'number' },
  { key: 'subTotal', title: 'Valor', type: 'currency' },
]

const vacio = {
  clienteCedula: '', nombre: '', negocio: '', estado: 'Nuevo',
  celular: '', telefono: '', email: '', direccion: '', barrioDireccion: '',
  residencia: '', barrioResidencia: '', geolocalizacion: '', zonaCodigo: '',
  posicionRuta: '', referencia1: '', referencia2: '',
  codeudorCedula: '', codeudorNombre: '', codeudorDireccion: '',
  codeudorTelefono: '', codeudorCelular: '',
  observaciones: '', fotoBase64: '', fotoContentType: '', firmaClienteBase64: '', firmaCodeudorBase64: '',
}

const ClienteViewPage: React.FC = () => {
  const { cedula } = useParams<{ cedula: string }>()
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [cliente, setCliente] = useState<any>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [prestamos, setPrestamos] = useState<any[]>([])
  const [facturas, setFacturas] = useState<any[]>([])
  const [cargandoHijas, setCargandoHijas] = useState(true)
  const [editarAbierto, setEditarAbierto] = useState(false)
  const [eliminarAbierto, setEliminarAbierto] = useState(false)
  const { aviso, setAviso } = usePendiente()
  const formRef = useRef<HTMLFormElement>(null)
  const [form, setForm] = useState(vacio)
  // Contadores de los tabs — SIEMPRE declarados (antes de los early returns:
  // React #310 si un useState aparece después de un return condicional).
  const [totalPrestamos, setTotalPrestamos] = useState(0)
  const [totalFacturas, setTotalFacturas] = useState(0)
  // Tab Facturas SOLO si la empresa tiene el módulo de facturación activo
  // (EmpresaModuloFacturacion — mismo guard de ClientesPage.tsx:219-225).
  // Reporte Rafael 2026-10-01: la vista mostraba el tab sin tener el módulo.
  const [modFacturacion, setModFacturacion] = useState(false)

  useEffect(() => {
    api.get<any>('/cuenta?empresa=' + empresa)
      .then((cuenta) => setModFacturacion(Number(cuenta.moduloFacturacion ?? 0) === 1
        // [RGM] 2026-10-07 (pedido Rafael): el tab Facturas del cliente también
        // aplica en motor SOLO FACTURACIÓN.
        || String(cuenta.modo ?? '') === 'FACTURACION'))
      .catch(() => undefined)
  }, [empresa])

  useEffect(() => {
    setCargando(true)
    setError('')
    api.get<any>(`/clientes/${cedula}?empresa=${empresa}`)
      .then(setCliente)
      .catch((e: any) => setError(e.message ?? 'Cliente no encontrado'))
      .finally(() => setCargando(false))
  }, [cedula, empresa])

  useEffect(() => {
    setCargandoHijas(true)
    Promise.all([
      api.get<any[]>(`/prestamos?empresa=${empresa}`),
      api.get<any[]>(`/facturas?empresa=${empresa}`),
    ])
      .then(([ps, fs]) => {
        setPrestamos(ps)
        setFacturas(fs)
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setCargandoHijas(false))
  }, [empresa])

  const prestamosDelCliente = useMemo(
    () => prestamos.filter((p) => String(p.clienteCedula ?? '') === String(cedula ?? '')),
    [prestamos, cedula],
  )
  const facturasDelCliente = useMemo(
    () => facturas.filter((f) => String(f.clienteCedula ?? '') === String(cedula ?? '')),
    [facturas, cedula],
  )

  const v = (valor: any): string => (valor === null || valor === undefined || valor === '' ? '—' : String(valor))

  const fila = (label: string, valor: any) => (
    <CCol md={6}>
      <div className="small text-body-secondary">{label}</div>
      <div className="fw-medium text-break">{v(valor)}</div>
    </CCol>
  )

  const firma = (tiene: boolean, base64: any) => {
    if (base64) {
      return (
        <img
          src={base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`}
          alt="Firma"
          style={{ maxWidth: 220, maxHeight: 90, borderRadius: 6 }}
        />
      )
    }
    return tiene ? 'Firma registrada' : 'Sin firma'
  }

  const tarjeta = (icono: any, titulo: string, contenido: React.ReactNode) => (
    <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
      <CCardHeader className=" d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
        <CIcon icon={icono} size="sm" /> {titulo}
      </CCardHeader>
      <CCardBody style={{ padding: '14px 16px' }}>
        <CRow className="g-3">{contenido}</CRow>
      </CCardBody>
    </CCard>
  )

  const setCampo = (campo: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [campo]: e.target.value })

  // [RGM] 2026-10-02 (E): campos de texto LIBRE descriptivos capitalizan la
  // primera letra de cada palabra; nombres/email/cédulas/códigos no se tocan.
  const setCampoCap = (campo: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [campo]: capitalizarPalabras(e.target.value) })

  const abrirEditar = () => {
    setError('')
    setForm({
      clienteCedula: String(cliente?.clienteCedula ?? ''),
      nombre: cliente?.clienteNombre ?? '',
      negocio: cliente?.clienteNegocio ?? '',
      estado: cliente?.clienteEstado ?? 'Nuevo',
      celular: cliente?.clienteCelular ?? '',
      telefono: '',
      email: cliente?.clienteEmail ?? '',
      direccion: cliente?.clienteDireccion ?? '',
      barrioDireccion: cliente?.clienteBarrioDireccion ?? '',
      residencia: cliente?.clienteResidencia ?? '',
      barrioResidencia: cliente?.clienteBarrioResidencia ?? '',
      geolocalizacion: cliente?.geolocalizacion ?? '',
      zonaCodigo: String(cliente?.zonaCodigo ?? ''),
      posicionRuta: String(cliente?.clientePosicionRuta ?? ''),
      referencia1: cliente?.referencia1 ?? '',
      referencia2: cliente?.referencia2 ?? '',
      codeudorCedula: String(cliente?.clienteCodeudorCedula ?? ''),
      codeudorNombre: cliente?.clienteCodeudorNombre ?? '',
      codeudorDireccion: cliente?.clienteCodeudorDireccion ?? '',
      codeudorTelefono: cliente?.clienteCodeudorTelefono ?? '',
      codeudorCelular: cliente?.clienteCodeudorCelular ?? '',
      observaciones: cliente?.clienteObservacion ?? '',
      fotoBase64: '', fotoContentType: '',
      firmaClienteBase64: '',
      firmaCodeudorBase64: '',
    })
    setEditarAbierto(true)
  }

  const guardar = (e: React.FormEvent) => {
    e.preventDefault()
    // Réplica de clienteww.cs:2902-2904 ('DO UPDATE' → BC Cliente UPD,
    // cliente_bc.cs:751-801) — el backend replica BC000910 con validaciones
    // CheckExtendedTable0928; foto/firmas van por R2 (diseño [RGM]).
    api.put(`/clientes/${cedula}`, {
      empresaCodigo: empresa,
      clienteNombre: form.nombre || undefined,
      clienteEstado: form.estado || undefined,
      clienteObservaciones: form.observaciones || undefined,
      clienteNegocio: form.negocio || undefined,
      clienteCelular: form.celular || undefined,
      clienteTelefono: form.telefono || undefined,
      clienteEmail: form.email || undefined,
      clienteDireccion: form.direccion || undefined,
      clienteBarrioDireccion: form.barrioDireccion || undefined,
      // ClienteReisdencia: typo GX preservado (type_SdtCliente.cs:243)
      clienteReisdencia: form.residencia || undefined,
      clienteBarrioResidencia: form.barrioResidencia || undefined,
      clienteGeolocalizacion: form.geolocalizacion || undefined,
      zonaCodigo: form.zonaCodigo ? Number(form.zonaCodigo) : undefined,
      posicionRuta: form.posicionRuta ? Number(form.posicionRuta) : undefined,
      clienteReferencia1: form.referencia1 || undefined,
      clienteReferencia2: form.referencia2 || undefined,
      codeudorCedula: form.codeudorCedula ? Number(form.codeudorCedula) : undefined,
      codeudorNombre: form.codeudorNombre || undefined,
      codeudorDireccion: form.codeudorDireccion || undefined,
      codeudorTelefono: form.codeudorTelefono || undefined,
      codeudorCelular: form.codeudorCelular || undefined,
      fotoBase64: form.fotoBase64 || undefined,
      fotoContentType: form.fotoContentType || undefined,
      firmaClienteBase64: form.firmaClienteBase64 || undefined,
      firmaCodeudorBase64: form.firmaCodeudorBase64 || undefined,
    })
      .then(() => {
        setAviso('Cliente modificado.')
        setEditarAbierto(false)
        // Recargar el detalle para reflejar los cambios
        api.get<any>(`/clientes/${cedula}?empresa=${empresa}`).then(setCliente).catch(() => undefined)
      })
      .catch((err: any) => setError(err.message ?? 'Error al modificar'))
  }

  const confirmarEliminar = () => {
    setEliminarAbierto(false)
    // Réplica de clienteww.cs:2910-2912 (Mode 'DLT' → BC000914 con
    // OnDeleteControls0928, cliente_bc.cs:874-916)
    api.delete(`/clientes/${cedula}?empresa=${empresa}`)
      .then(() => {
        setAviso('Cliente eliminado.')
        navigate('/clientes')
      })
      .catch((err: any) => setError(err.message ?? 'Error al eliminar'))
  }

  if (cargando) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
      </div>
    )
  }

  if (error && !cliente) {
    return (
      <>
        <CRow className="mb-4">
          <CCol className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">Cliente</h4>
            <CButton color="secondary" variant="outline" onClick={() => navigate('/clientes')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
          </CCol>
        </CRow>
        <CAlert color="warning">{error} — Cliente no encontrado</CAlert>
      </>
    )
  }

  return (
    <>
      <CRow className="mb-4">
        <CCol className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h4 className="mb-0">Cliente {cedula}</h4>
          <small className="text-body-secondary">{totalPrestamos + totalFacturas} {totalPrestamos + totalFacturas === 1 ? 'registro' : 'registros'}</small>
          </div>
          <div className="d-flex gap-2">
            <CButton color="secondary" variant="outline" onClick={() => navigate('/clientes')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
            <CButton color="primary" onClick={abrirEditar}>
              <CIcon icon={cilPen} className="me-1" size="sm" />
              Modificar
            </CButton>
            <CButton color="danger" onClick={() => setEliminarAbierto(true)}>
              <CIcon icon={cilTrash} className="me-1" size="sm" />
              Eliminar
            </CButton>
          </div>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      {tarjeta(cilUser, 'Información General', (
        <>
          {fila('Fecha creación', cliente?.clienteFechaCreacion)}
          {fila('Creado por', cliente?.clienteCreadoPor)}
          {fila('Nombre completo', cliente?.clienteNombre)}
          {fila('Cédula', cliente?.clienteCedula)}
          {fila('Negocio', cliente?.clienteNegocio)}
          {fila('Estado', cliente?.clienteEstado)}
          {fila('Observa', cliente?.clienteObservacion)}
          {fila('#', cliente?.clienteCantidadPrestamos)}
        </>
      ))}

      {tarjeta(cilPhone, 'Contacto', (
        <>
          {fila('Celular / WhatsApp', cliente?.clienteCelular)}
          {fila('Email', cliente?.clienteEmail)}
        </>
      ))}

      {tarjeta(cilLocationPin, 'Direcciones', (
        <>
          {fila('Dirección negocio', cliente?.clienteDireccion)}
          {fila('Barrio', cliente?.clienteBarrioDireccion)}
          {fila('Dirección residencia', cliente?.clienteResidencia)}
          {fila('Barrio residencia', cliente?.clienteBarrioResidencia)}
          {fila('Geolocalización', cliente?.geolocalizacion)}
        </>
      ))}

      {tarjeta(cilMap, 'Ruta', (
        <>
          {fila('Ruta', cliente?.zonaNombre)}
          {fila('Posición ruta', cliente?.clientePosicionRuta)}
        </>
      ))}

      {tarjeta(cilAddressBook, 'Referencias', (
        <>
          {fila('Referencia 1', cliente?.referencia1)}
          {fila('Referencia 2', cliente?.referencia2)}
        </>
      ))}

      {tarjeta(cilUserFollow, 'Codeudor', (
        <>
          {fila('Nombre', cliente?.clienteCodeudorNombre)}
          {fila('Cédula', cliente?.clienteCodeudorCedula)}
          {fila('Celular', cliente?.clienteCodeudorCelular)}
          {fila('Dirección', cliente?.clienteCodeudorDireccion)}
        </>
      ))}

      {tarjeta(cilPencil, 'Observaciones y Firmas', (
        <>
          <CCol md={12}>
            <div className="small text-body-secondary">Observaciones</div>
            <div className="fw-medium text-break">{v(cliente?.clienteObservacion)}</div>
          </CCol>
          <CCol md={6}>
            <div className="small text-body-secondary">Firma Cliente</div>
            <div className="fw-medium">{firma(cliente?.tieneFirma, cliente?.firmaClienteBase64)}</div>
          </CCol>
          <CCol md={6}>
            <div className="small text-body-secondary">Firma Codeudor</div>
            <div className="fw-medium">{firma(cliente?.tieneFirmaCodeudor, cliente?.firmaCodeudorBase64)}</div>
          </CCol>
        </>
      ))}

      <CTabs defaultActiveItemKey="prestamos">
        <CTabList variant="tabs">
          <CTab itemKey="prestamos">Préstamos</CTab>
          {modFacturacion && <CTab itemKey="facturas">Facturas</CTab>}
        </CTabList>
        <CTabPanel itemKey="prestamos" className="pt-3">
          {cargandoHijas ? (
            <div className="text-center py-4">
              <CSpinner color="primary" />
            </div>
          ) : (
            <WwGrid
              title="Préstamos"
              columns={columnasPrestamos}
              rows={prestamosDelCliente}
        onTotalChange={setTotalPrestamos}
              onAgregar={() => navigate('/prestamos')}
            />
          )}
        </CTabPanel>
        {modFacturacion && (
          <CTabPanel itemKey="facturas" className="pt-3">
            {cargandoHijas ? (
              <div className="text-center py-4">
                <CSpinner color="primary" />
              </div>
            ) : (
              <WwGrid
                title="Facturas"
                columns={columnasFacturas}
                rows={facturasDelCliente}
          onTotalChange={setTotalFacturas}
                onAgregar={() => navigate('/facturas')}
              />
            )}
          </CTabPanel>
        )}
      </CTabs>

      <CModal visible={editarAbierto} onClose={() => setEditarAbierto(false)} size="lg" className="ww-modal-cliente">
        <CModalHeader>
          <CModalTitle>Modificar cliente {cedula}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          <CForm ref={formRef} onSubmit={guardar}>
            {tarjeta(cilUser, 'Información General', (
              <>
                <CCol md={6}>
                  <CFormLabel>Nombre Completo</CFormLabel>
                  {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                  <CFormInput value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Cédula / NUIP / ID</CFormLabel>
                  <CFormInput type="number" value={form.clienteCedula} onChange={setCampo('clienteCedula')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Negocio</CFormLabel>
                  <CFormInput value={form.negocio} onChange={setCampoCap('negocio')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Estado</CFormLabel>
                  <CFormSelect value={form.estado} onChange={setCampo('estado')}>
                    {estados.map((e) => <option key={e} value={e}>{e}</option>)}
                  </CFormSelect>
                </CCol>
              </>
            ))}

            {tarjeta(cilPhone, 'Contacto', (
              <>
                <CCol md={12}>
                  <CFormLabel>Celular / WhatsApp</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{prefijoPais()}</CInputGroupText>
                    <CFormInput value={form.celular} onChange={setCampo('celular')} />
                  </CInputGroup>
                </CCol>
                <CCol md={12}>
                  <CFormLabel>Email</CFormLabel>
                  <CFormInput type="email" value={form.email} onChange={setCampo('email')} />
                </CCol>
              </>
            ))}

            {tarjeta(cilLocationPin, 'Direcciones', (
              <>
                <CCol md={6}>
                  <CFormLabel>Dirección Negocio</CFormLabel>
                  <CFormInput value={form.direccion} onChange={setCampoCap('direccion')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Barrio</CFormLabel>
                  <CFormInput value={form.barrioDireccion} onChange={setCampoCap('barrioDireccion')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Dirección Residencia</CFormLabel>
                  <CFormInput value={form.residencia} onChange={setCampoCap('residencia')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Barrio Residencia</CFormLabel>
                  <CFormInput value={form.barrioResidencia} onChange={setCampoCap('barrioResidencia')} />
                </CCol>
                <CCol md={12}>
                  <CFormLabel>Geolocalización</CFormLabel>
                  <CFormInput placeholder="lat,lng" value={form.geolocalizacion} onChange={setCampo('geolocalizacion')} />
                </CCol>
              </>
            ))}

            {tarjeta(cilMap, 'Ruta', (
              <>
                <CCol md={6}>
                  <CFormLabel>Ruta</CFormLabel>
                  <CFormInput value={form.zonaCodigo} onChange={setCampo('zonaCodigo')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Posición Ruta</CFormLabel>
                  <CFormInput type="number" value={form.posicionRuta} onChange={setCampo('posicionRuta')} />
                </CCol>
              </>
            ))}

            {tarjeta(cilAddressBook, 'Referencias', (
              <>
                <CCol md={6}>
                  <CFormLabel>Referencia 1</CFormLabel>
                  <CFormInput value={form.referencia1} onChange={setCampoCap('referencia1')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Referencia 2</CFormLabel>
                  <CFormInput value={form.referencia2} onChange={setCampoCap('referencia2')} />
                </CCol>
              </>
            ))}

            {tarjeta(cilUserFollow, 'Codeudor', (
              <>
                <CCol md={6}>
                  <CFormLabel>Nombre</CFormLabel>
                  {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                  <CFormInput value={form.codeudorNombre} onChange={(e) => setForm({ ...form, codeudorNombre: e.target.value.toUpperCase() })} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Cédula</CFormLabel>
                  <CFormInput type="number" value={form.codeudorCedula} onChange={setCampo('codeudorCedula')} />
                </CCol>
                <CCol md={12}>
                  <CFormLabel>Celular</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{prefijoPais()}</CInputGroupText>
                    <CFormInput value={form.codeudorCelular} onChange={setCampo('codeudorCelular')} />
                  </CInputGroup>
                </CCol>
                <CCol md={12}>
                  <CFormLabel>Dirección</CFormLabel>
                  <CFormInput value={form.codeudorDireccion} onChange={setCampoCap('codeudorDireccion')} />
                </CCol>
              </>
            ))}

            {tarjeta(cilPencil, 'Observaciones y Firmas', (
              <>
                <CCol md={12}>
                  <CFormLabel>Observaciones</CFormLabel>
                  <CFormTextarea rows={3} value={form.observaciones} onChange={setCampoCap('observaciones')} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Firma Cliente</CFormLabel>
                  <FirmaCanvas
                    value={form.firmaClienteBase64}
                    onChange={(b64) => setForm({ ...form, firmaClienteBase64: b64 ?? '' })}
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Firma Codeudor</CFormLabel>
                  <FirmaCanvas
                    value={form.firmaCodeudorBase64}
                    onChange={(b64) => setForm({ ...form, firmaCodeudorBase64: b64 ?? '' })}
                  />
                </CCol>
              </>
            ))}
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => setEditarAbierto(false)}>Cancelar</CButton>
          <CButton color="primary" type="button" onClick={() => formRef.current?.requestSubmit()}>
            Guardar
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal visible={eliminarAbierto} onClose={() => setEliminarAbierto(false)}>
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilWarning} className="me-1 text-danger" />
            Eliminar cliente
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="mb-0">
            ¿Eliminar el cliente <strong>{cliente?.clienteNombre ?? ''}</strong> (cédula {cedula})?
          </p>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => setEliminarAbierto(false)}>Cancelar</CButton>
          <CButton color="danger" type="button" onClick={confirmarEliminar}>
            Eliminar
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default ClienteViewPage
